import { useTranslation } from 'react-i18next'
import { MarqueeColumn } from '@marketing/components'

interface AnimationFeatureSectionProps {
  titleHighlight: string
  title: string
  body: string
  emphasis: string
  columnItemCount: number
}

const QUETURE_IMAGES = Array.from(
  { length: 18 },
  (_, i) => `/marketing/franchise/queture-${String(i + 1).padStart(2, '0')}.webp`,
)

function ThumbCard({ src, index }: { src: string; index: number }) {
  const { t: mt } = useTranslation('marketing')

  return (
    <div className="w-full aspect-[16/9] rounded-[12px] lg:rounded-[16px] overflow-hidden">
      <img
        src={src}
        alt={mt('media.thumbnail', { number: index + 1 })}
        loading="lazy"
        className="w-full h-full object-cover"
      />
    </div>
  )
}

/**
 * #04 Animation feature — left text + right 3-col vertical marquee.
 * Figma: node 37:12525 (desktop) / 194:6080 (mobile)
 */
export function AnimationFeatureSection({
  titleHighlight,
  title,
  body,
  emphasis,
  columnItemCount,
}: AnimationFeatureSectionProps) {
  const col1Src = QUETURE_IMAGES.slice(0, 6)
  const col2Src = QUETURE_IMAGES.slice(6, 12)
  const col3Src = QUETURE_IMAGES.slice(12, 18)

  const col1Items = Array.from({ length: columnItemCount }, (_, i) => (
    <ThumbCard key={i} src={col1Src[i % col1Src.length]} index={i} />
  ))
  const col2Items = Array.from({ length: columnItemCount }, (_, i) => (
    <ThumbCard key={i} src={col2Src[i % col2Src.length]} index={i + columnItemCount} />
  ))
  const col3Items = Array.from({ length: columnItemCount }, (_, i) => (
    <ThumbCard key={i} src={col3Src[i % col3Src.length]} index={i + columnItemCount * 2} />
  ))

  return (
    <section id="animation-feature" data-marketing-section="" className="w-full bg-qb-bg overflow-x-clip">
      <div className="mx-auto max-w-[1136px] py-20 lg:py-[100px]">
        <div data-testid="text-image-split" className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-10">
          {/* Left: text */}
          <div className="flex flex-col gap-10 lg:w-[548px] shrink-0 px-6 lg:px-0 text-center lg:text-left">
            <div className="flex flex-col gap-2">
              <p className="font-['Pretendard',sans-serif] font-bold text-[32px] lg:text-[40px] leading-[1.2] tracking-[-0.6px] text-qb-primary">
                {titleHighlight}
              </p>
              {title.split('\n').map((line, i) => (
                <p
                  key={i}
                  className="font-['Pretendard',sans-serif] font-bold text-[32px] lg:text-[40px] leading-[1.2] tracking-[-0.6px] text-qb-text-high"
                >
                  {line}
                </p>
              ))}
            </div>
            <div className="flex flex-col gap-10">
              <p className="font-['Pretendard',sans-serif] text-[18px] lg:text-[20px] font-normal leading-[1.5] tracking-[-0.6px] text-qb-text-mid whitespace-pre-line">
                {body}
              </p>
              <p className="font-['Pretendard',sans-serif] font-bold text-[22px] lg:text-[28px] leading-[1.4] tracking-[-0.6px] text-qb-text-high whitespace-pre-line">
                {emphasis}
              </p>
            </div>
          </div>

          {/* Right: 3-column vertical marquee.
              Desktop: 754px wide (Figma node 37:12527) — intentionally bleeds past
              the centered 1136px container; section uses overflow-x-clip to prevent
              horizontal scroll. All 3 columns fit fully on ≥1920px viewports;
              narrower desktops let the trailing column fade out on the right edge. */}
          <div className="w-full lg:w-[754px] lg:shrink-0 max-h-[480px] lg:max-h-[600px] overflow-hidden relative flex justify-center">
            <div
              className="grid gap-3 lg:gap-4 h-full shrink-0 [grid-template-columns:repeat(3,181px)] lg:[grid-template-columns:repeat(3,240px)]"
            >
              <MarqueeColumn height={600} speed="slow" gap={16}>
                {col1Items}
              </MarqueeColumn>
              <MarqueeColumn height={600} speed="normal" reverse gap={16}>
                {col2Items}
              </MarqueeColumn>
              <MarqueeColumn height={600} speed="slow" gap={16}>
                {col3Items}
              </MarqueeColumn>
            </div>
            {/* Top/bottom fade masks */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-qb-bg to-transparent"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-qb-bg to-transparent"
            />
          </div>
        </div>
      </div>
    </section>
  )
}
